import { useRef } from 'react';
import clsx from 'clsx';

interface ResizeHandleProps {
  /** What is being resized, for screen readers ("Navigation width"). */
  label: string;
  width: number;
  min: number;
  max: number;
  onResize: (width: number) => void;
  onReset: () => void;
  className?: string;
}

/**
 * The drag edge on the right of a resizable column. Drag it, or focus it and
 * use the arrow keys (Shift for bigger steps), Home and End; double-click
 * puts the default width back. It shows an orange bar while hovered, focused
 * or dragged, and is otherwise invisible.
 */
export function ResizeHandle({ label, width, min, max, onResize, onReset, className }: ResizeHandleProps) {
  const start = useRef<{ x: number; w: number } | null>(null);

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={label}
      aria-valuenow={width}
      aria-valuemin={min}
      aria-valuemax={max}
      tabIndex={0}
      title="Drag to resize. Double-click to reset."
      onPointerDown={(e) => {
        if (e.button !== 0) return;
        e.preventDefault();
        e.currentTarget.setPointerCapture(e.pointerId);
        start.current = { x: e.clientX, w: width };
        document.body.classList.add('ap-resizing');
      }}
      onPointerMove={(e) => {
        if (start.current) onResize(start.current.w + e.clientX - start.current.x);
      }}
      onPointerUp={(e) => {
        start.current = null;
        e.currentTarget.releasePointerCapture(e.pointerId);
        document.body.classList.remove('ap-resizing');
      }}
      onPointerCancel={() => {
        start.current = null;
        document.body.classList.remove('ap-resizing');
      }}
      onDoubleClick={onReset}
      onKeyDown={(e) => {
        const step = e.shiftKey ? 48 : 16;
        if (e.key === 'ArrowLeft') onResize(width - step);
        else if (e.key === 'ArrowRight') onResize(width + step);
        else if (e.key === 'Home') onResize(min);
        else if (e.key === 'End') onResize(max);
        else return;
        e.preventDefault();
      }}
      className={clsx('ap-resize', className)}
    />
  );
}
